import React from 'react';
import styled from 'styled-components';
import { getWorkListPreviewColumnTemplate } from './DocumentWorkListHorizontal';

export interface WorkListA4PreviewVerticalProps {
  logoSrc: string;
  title: string;
  subtitle: string;
  gallery: string;
  baseItemsPerPage: number;
  selectedCount: number;
  /** 향후 다페이지 대비. 현재는 1만 사용해도 됨. */
  currentPage?: number;
  includeArtist: boolean;
  includePrice: boolean;
  includeNote: boolean;
  pageNumberLabel: string;
}

/** 1페이지에 들어갈 수 있는 최대 작품 수(헤더·푸터 등으로 `baseItemsPerPage`보다 2칸 적음). */
export const getWorkListFirstPageCapacity = (baseItemsPerPage: number): number =>
  Math.max(1, baseItemsPerPage - 2);

export const getWorkListRowsOnPage = (
  baseItemsPerPage: number,
  currentPage: number
): number =>
  currentPage === 1 ? getWorkListFirstPageCapacity(baseItemsPerPage) : baseItemsPerPage;

/** 현재 페이지에 해당하는 선택 목록의 시작 인덱스(0-based). */
export const getWorkListPageStartIndex = (
  baseItemsPerPage: number,
  currentPage: number
): number => {
  const first = getWorkListFirstPageCapacity(baseItemsPerPage);
  if (currentPage === 1) return 0;
  return first + (currentPage - 2) * baseItemsPerPage;
};

/** 선택 개수 기준 PDF 총 페이지 수(미리보기 요약과 동일 규칙). */
export const getWorkListPdfTotalPageCount = (
  selectedCount: number,
  baseItemsPerPage: number
): number => {
  if (selectedCount <= 0) return 1;
  const first = getWorkListFirstPageCapacity(baseItemsPerPage);
  if (selectedCount <= first) return 1;
  return 1 + Math.ceil((selectedCount - first) / baseItemsPerPage);
};

const WorkListA4PreviewVertical: React.FC<WorkListA4PreviewVerticalProps> = ({
  logoSrc,
  title,
  subtitle,
  gallery,
  baseItemsPerPage,
  selectedCount,
  currentPage = 1,
  includeArtist,
  includePrice,
  includeNote,
  pageNumberLabel,
}) => {
  const columnTemplate = getWorkListPreviewColumnTemplate(
    includeArtist,
    includePrice,
    includeNote,
  );
  const rowsOnThisPage = getWorkListRowsOnPage(baseItemsPerPage, currentPage);
  const startIndex = getWorkListPageStartIndex(baseItemsPerPage, currentPage);
  const itemsFromSelectionOnPage = Math.max(0, selectedCount - startIndex);
  const filledGrayRows = Math.min(itemsFromSelectionOnPage, rowsOnThisPage);

  return (
    <A4PreviewSlot>
      <A4Portrait>
        <LogoInA4 src={logoSrc} alt="" />
        <TitleAndSubTitleSubTitle>
          <TitleInA4>{title}</TitleInA4>
          <SubTitleInA4>{subtitle}</SubTitleInA4>
        </TitleAndSubTitleSubTitle>
        <TableAreaInA4 $isFirstPage={currentPage === 1}>
          <TableHeaderRow $columnTemplate={columnTemplate}>
            <HeaderCell>No.</HeaderCell>
            <HeaderCell>Image</HeaderCell>
            {includeArtist && <HeaderCell>Artist</HeaderCell>}
            <HeaderCell>Title</HeaderCell>
            <HeaderCell>Year</HeaderCell>
            <HeaderCell>Material</HeaderCell>
            <HeaderCell>Size</HeaderCell>
            {includePrice && <HeaderCell>Price</HeaderCell>}
            {includeNote && <HeaderCell>Note</HeaderCell>}
          </TableHeaderRow>

          <TableBody>
            {Array.from({ length: rowsOnThisPage }, (_, rowIndex) => {
              const isFilled = rowIndex < filledGrayRows;
              const globalIndex = startIndex + rowIndex + 1;
              return (
                <TableRowShell key={`work-list-row-shell-${rowIndex}`} $columnTemplate={columnTemplate}>
                  {isFilled ? (
                    <RowIndexCell>{globalIndex}</RowIndexCell>
                  ) : (
                    <EmptyGridSlot aria-hidden />
                  )}
                  {isFilled ? (
                    <>
                      <ThumbShell />
                      {includeArtist && <ArtistShell />}
                      <TitleShell />
                      <YearShell />
                      <MaterialShell />
                      <SizeShell />
                      {includePrice && <PriceShell />}
                      {includeNote && <NoteShell />}
                    </>
                  ) : (
                    <>
                      <EmptyGridSlot aria-hidden />
                      {includeArtist && <EmptyGridSlot aria-hidden />}
                      <EmptyGridSlot aria-hidden />
                      <EmptyGridSlot aria-hidden />
                      <EmptyGridSlot aria-hidden />
                      <EmptyGridSlot aria-hidden />
                      {includePrice && <EmptyGridSlot aria-hidden />}
                      {includeNote && <EmptyGridSlot aria-hidden />}
                    </>
                  )}
                </TableRowShell>
              );
            })}
          </TableBody>
        </TableAreaInA4>
        <GalleryInA4>{gallery}</GalleryInA4>
        <PageNumberInA4>{pageNumberLabel}</PageNumberInA4>
      </A4Portrait>
    </A4PreviewSlot>
  );
};

/** 미리보기 슬롯 크기 안에서 항상 A4 세로 비율(210:297)을 유지한다. */
const A4PreviewSlot = styled.div`
  width: 100%;
  height: 70%;
  min-height: 0;
  max-height: 50vh;
  container-type: size;
  container-name: work-list-a4-slot-vertical;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const A4Portrait = styled.div`
  position: relative;
  box-sizing: border-box;
  aspect-ratio: 210 / 297;
  width: min(100cqw, calc(100cqh * 210 / 297));
  max-width: 100%;
  max-height: 100%;
  flex-shrink: 0;
  border: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
  background-color: ${({ theme }) => theme.colors.color_Alpha_04};
`;

const LogoInA4 = styled.img`
  position: absolute;
  top: 1vh;
  right: 1vh;
  width: 1.8vh;
  height: auto;
  object-fit: contain;
`;

const TitleAndSubTitleSubTitle = styled.div`
  position: absolute;
  top: 1vh;
  left: 1vh;
  display: flex;
  flex-direction: column;
`;

const TitleInA4 = styled.div`
  ${({ theme }) => theme.typography.Footer_01};
`;
const SubTitleInA4 = styled.div`
  ${({ theme }) => theme.typography.Footer_02};
`;

const ROW_H = 'clamp(20px, 5vh, 35px)';

const TableAreaInA4 = styled.div<{ $isFirstPage: boolean }>`
  position: absolute;
  top: 4.5vh;
  left: 1vh;
  right: 1vh;
  bottom: 3.2vh;
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding-top: ${({ $isFirstPage }) => ($isFirstPage ? `calc(1.5 * ${ROW_H})` : '0')};
  box-sizing: border-box;
`;

const TableHeaderRow = styled.div<{ $columnTemplate: string }>`
  display: grid;
  grid-template-columns: ${({ $columnTemplate }) => $columnTemplate};
  align-items: center;
  column-gap: 0.8vh;
  height: ${ROW_H};
  padding: 0 0.6vh;
  box-sizing: border-box;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_05};
`;

const HeaderCell = styled.div`
  ${({ theme }) => theme.typography.Footer_02};
  color: ${({ theme }) => theme.colors.color_Gray_03};
  opacity: 0.72;
  white-space: nowrap;
`;

const TableBody = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
`;

const TableRowShell = styled.div<{ $columnTemplate: string }>`
  display: grid;
  grid-template-columns: ${({ $columnTemplate }) => $columnTemplate};
  align-items: center;
  column-gap: 0.8vh;
  height: ${ROW_H};
  padding: 0 0.6vh;
  box-sizing: border-box;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_05};
`;

const CellShell = styled.div`
  background: ${({ theme }) => theme.colors.color_Gray_05};
  opacity: 0.3;
  border-radius: 1px;
`;

const ThumbShell = styled(CellShell)`
  width: 100%;
  height: 70%;
  min-height: 8px;
`;

const TitleShell = styled(CellShell)`
  width: 100%;
  height: 38%;
  min-height: 6px;
`;

const ArtistShell = styled(CellShell)`
  width: 100%;
  height: 38%;
  min-height: 6px;
`;

const YearShell = styled(CellShell)`
  width: 100%;
  height: 38%;
  min-height: 6px;
`;

const MaterialShell = styled(CellShell)`
  width: 100%;
  height: 38%;
  min-height: 6px;
`;

const SizeShell = styled(CellShell)`
  width: 100%;
  height: 38%;
  min-height: 6px;
`;

const PriceShell = styled(CellShell)`
  width: 100%;
  height: 38%;
  min-height: 6px;
`;

const NoteShell = styled(CellShell)`
  width: 100%;
  height: 38%;
  min-height: 6px;
`;

/** 데이터 없는 칸 — 회색·테두리 없음(행 구분선만 `TableRowShell`에 유지) */
const EmptyGridSlot = styled.div`
  min-width: 0;
  min-height: 0;
`;

const RowIndexCell = styled.div`
  ${({ theme }) => theme.typography.Footer_02};
  color: ${({ theme }) => theme.colors.color_Gray_03};
  opacity: 0.8;
`;

/**
 * 열 너비 비율은 `DocumentWorkListHorizontal.getWorkListPreviewColumnTemplate` 과 동일.
 */

const GalleryInA4 = styled.div`
  position: absolute;
  left: 1vh;
  bottom: 1vh;
  ${({ theme }) => theme.typography.Footer_02};
`;

const PageNumberInA4 = styled.div`
  position: absolute;
  right: 1vh;
  bottom: 1vh;
  ${({ theme }) => theme.typography.Footer_02};
`;

export default WorkListA4PreviewVertical;
